export type ListingPhotoIssueDefinition = {
  id: string;
  label: string;
  description: string;
  messageText: string;
};

/** Preset slugs tied to listing photo quality (Phase 5 admin ↔ seller loop). */
export const LISTING_PHOTO_FEEDBACK_SLUGS = [
  "image_quality",
  "image_quality_main",
  "image_quality_photo_2",
  "image_quality_photo_3",
] as const;

export type ListingPhotoFeedbackSlug =
  (typeof LISTING_PHOTO_FEEDBACK_SLUGS)[number];

export type ListingPhotoSlotIndex = 1 | 2 | 3;

/** Slot index → preset slug (D15). */
export const LISTING_PHOTO_SLOT_SLUG: Record<
  ListingPhotoSlotIndex,
  Exclude<ListingPhotoFeedbackSlug, "image_quality">
> = {
  1: "image_quality_main",
  2: "image_quality_photo_2",
  3: "image_quality_photo_3",
};

export const ALL_LISTING_PHOTOS_SLUG: ListingPhotoFeedbackSlug =
  "image_quality";

export function isListingPhotoFeedbackSlug(
  slug: string,
): slug is ListingPhotoFeedbackSlug {
  return (LISTING_PHOTO_FEEDBACK_SLUGS as readonly string[]).includes(slug);
}

export type ListingImageForSlotOrder = {
  url: string;
  position?: number | null;
  is_main?: boolean | null;
};

export type OrderedListingPhotoSlot = {
  slot: ListingPhotoSlotIndex;
  url: string;
};

/**
 * Order listing images main-first then position — matches review-round snapshot slots.
 */
export function orderItemImagesForReviewSlots(
  images: ListingImageForSlotOrder[],
): OrderedListingPhotoSlot[] {
  const sorted = [...images].sort((a, b) => {
    if (a.is_main && !b.is_main) return -1;
    if (!a.is_main && b.is_main) return 1;
    return (a.position ?? 0) - (b.position ?? 0);
  });
  return sorted.slice(0, 3).map((row, index) => ({
    slot: (index + 1) as ListingPhotoSlotIndex,
    url: row.url,
  }));
}

export function stripListingPhotoFeedbackSlugs(
  codes: Iterable<string>,
): Set<string> {
  const next = new Set<string>();
  for (const code of codes) {
    if (!isListingPhotoFeedbackSlug(code)) {
      next.add(code);
    }
  }
  return next;
}

export function photoSlotsFromReasonCodes(
  reasonCodes: Iterable<string>,
): ListingPhotoSlotIndex[] {
  const slots: ListingPhotoSlotIndex[] = [];
  for (const code of reasonCodes) {
    if (code === ALL_LISTING_PHOTOS_SLUG) {
      return [];
    }
    for (const [slot, slug] of Object.entries(LISTING_PHOTO_SLOT_SLUG) as [
      string,
      string,
    ][]) {
      if (slug === code) {
        slots.push(Number(slot) as ListingPhotoSlotIndex);
      }
    }
  }
  return slots.sort((a, b) => a - b);
}

export function hasAllPhotosFeedbackSlug(
  reasonCodes: Iterable<string>,
): boolean {
  return [...reasonCodes].includes(ALL_LISTING_PHOTOS_SLUG);
}

/** Toggle one slot; clears generic all-photos slug. Slot-specific slugs are mutually combinable. */
export function togglePhotoSlotInReasonCodes(
  codes: Set<string>,
  slot: ListingPhotoSlotIndex,
): Set<string> {
  const next = stripListingPhotoFeedbackSlugs(codes);
  const selectedSlots = new Set(photoSlotsFromReasonCodes(codes));
  if (selectedSlots.has(slot)) {
    selectedSlots.delete(slot);
  } else {
    selectedSlots.add(slot);
  }
  for (const s of [...selectedSlots].sort((a, b) => a - b)) {
    next.add(LISTING_PHOTO_SLOT_SLUG[s]);
  }
  return next;
}

/** Select or clear all-photos preset; mutually exclusive with slot-specific slugs. */
export function setAllPhotosFeedbackInReasonCodes(
  codes: Set<string>,
  enabled: boolean,
): Set<string> {
  const next = stripListingPhotoFeedbackSlugs(codes);
  if (enabled) {
    next.add(ALL_LISTING_PHOTOS_SLUG);
  }
  return next;
}

/** Short tips appended to WhatsApp / email when a photo preset is selected. */
export const LISTING_PHOTO_CHANNEL_TIPS =
  "Photo tips: use daylight or a bright room, wipe the lens, use a plain background, and show the whole item.";

/**
 * Listing-issue rows for improvement presets (needs_user_action context).
 * Slot-specific slugs let admins cite photo 1, photo 2, or photo 3.
 */
export function getListingPhotoIssueDefinitions(): ListingPhotoIssueDefinition[] {
  return [
    {
      id: "image_quality",
      label: "All photos",
      description: "Applies to every photo: the cover and photos 2 and 3.",
      messageText: [
        "* All photos — Replace every photo so each one is sharp, well lit, and shows this item (not a stock photo or an item still in the box).",
        "* Photo 1 (cover) — A front view of the whole item. Buyers see this picture in search.",
        "* Photo 2 — A close-up of the brand, label, or condition.",
        "* Photo 3 — Another angle, the size, or any wear buyers should see.",
      ].join("\n"),
    },
    {
      id: "image_quality_main",
      label: "Photo 1 (cover)",
      description: "Applies only to photo 1, the cover buyers see in search.",
      messageText:
        "* Photo 1 (cover) — Replace this photo with a sharp, well-lit front view of the whole item. Buyers see this picture first in search.",
    },
    {
      id: "image_quality_photo_2",
      label: "Photo 2",
      description: "Applies only to the second photo.",
      messageText:
        "* Photo 2 — Replace this photo with a clear close-up of the brand, label, or condition.",
    },
    {
      id: "image_quality_photo_3",
      label: "Photo 3",
      description: "Applies only to the third photo.",
      messageText:
        "* Photo 3 — Replace this photo with another angle, a sense of size, or a clear view of any wear.",
    },
  ];
}

/** Seller-facing hint when status is needs_user_action and feedback includes photo presets. */
export function getPhotoResubmissionHint(
  reasonCodes?: string[] | null,
): string | null {
  if (!reasonCodes?.some(isListingPhotoFeedbackSlug)) {
    return null;
  }
  const hasSlotSpecific = reasonCodes.some(
    (c) => c !== "image_quality" && isListingPhotoFeedbackSlug(c),
  );
  if (hasSlotSpecific) {
    return "Replace the photo called out below. Open Edit listing, swap that photo, then save to send it back for review.";
  }
  return "Update all three photos as described below, then save to send the listing back for review.";
}

export function appendPhotoTipsToFeedbackSummary(
  summary: string,
  reasonCodes?: string[] | null,
): string {
  if (!reasonCodes?.some(isListingPhotoFeedbackSlug)) {
    return summary;
  }
  const trimmed = summary.trim();
  if (trimmed.includes(LISTING_PHOTO_CHANNEL_TIPS)) {
    return trimmed;
  }
  return trimmed
    ? `${trimmed}\n\n${LISTING_PHOTO_CHANNEL_TIPS}`
    : LISTING_PHOTO_CHANNEL_TIPS;
}
